/**
 * Domain entity schemas — the wire shapes for every resource the API exposes.
 */

import { z } from 'zod';
import {
  CefrSchema,
  ContentIdSchema,
  CorrectionStyleSchema,
  DialectSchema,
  DisputeReasonSchema,
  EpochMsSchema,
  InterfaceLocaleSchema,
  ItemTypeSchema,
  LanguageSchema,
  LocalDateSchema,
  MediaUrlSchema,
  ModalitySchema,
  PersonaIdSchema,
  PillarSchema,
  ScaffoldingSchema,
  Score100Schema,
  ScoreBandSchema,
  ScoreDimensionsSchema,
  ScoreStatusSchema,
  SessionKindSchema,
  SessionModeSchema,
  TierSchema,
  TimezoneSchema,
  UlidSchema,
  Unit01Schema,
} from './primitives.js';

// ── User ──────────────────────────────────────────────────────────────────────

export const UserSchema = z.object({
  id: UlidSchema,
  kind: z.enum(['anonymous', 'registered']),
  email: z.string().email().nullable(),
  displayName: z.string().max(40).nullable(),
  interfaceLocale: InterfaceLocaleSchema,
  nativeLanguage: LanguageSchema.nullable(),
  timezone: TimezoneSchema,
  createdAt: EpochMsSchema,
  privacy: z.object({
    /** Default is `keep_30d`; the private-by-default choice. */
    recordingPolicy: z.enum(['keep', 'keep_30d', 'delete_after_scoring']),
    /** Model-training consent. Default OFF, always. */
    trainingOptIn: z.boolean(),
    analyticsOptOut: z.boolean(),
  }),
});
export type User = z.infer<typeof UserSchema>;

// ── Course ────────────────────────────────────────────────────────────────────

export const CourseSchema = z.object({
  id: UlidSchema,
  language: LanguageSchema,
  dialect: DialectSchema,
  cefrLevel: CefrSchema,
  /** Shown to the learner as a band, never hidden. */
  cefrConfidence: Unit01Schema,
  cefrBandSublevels: z.number().int().min(0).max(3),
  vocabEstimate: z.number().int().nonnegative(),
  goals: z.array(z.enum(['travel', 'work', 'family', 'exam', 'moving', 'curiosity'])).max(2),
  examTarget: z
    .enum(['ielts', 'toefl', 'dele', 'delf', 'jlpt', 'hsk', 'topik', 'goethe'])
    .nullable(),
  examDate: EpochMsSchema.nullable(),
  dailyGoalMinutes: z.number().int().min(1).max(240),
  scaffolding: ScaffoldingSchema,
  persona: z.object({
    id: PersonaIdSchema,
    strictness: Unit01Schema,
    correctionStyle: CorrectionStyleSchema,
    speechRate: z.number().min(0.5).max(1.5),
  }),
  isActive: z.boolean(),
  /** Optimistic-concurrency token; three clients may edit this row. */
  rowV: z.number().int().positive(),
});
export type Course = z.infer<typeof CourseSchema>;

export const SkillScoreSchema = z.object({
  pillar: PillarSchema,
  score: Score100Schema,
  /** Shown in the UI as "last evaluated"; staleness is disclosed, not hidden. */
  evaluatedAt: EpochMsSchema,
  sampleSize: z.number().int().nonnegative(),
  /** Change over the trailing 7 days, in points. */
  delta7d: z.number(),
});
export type SkillScore = z.infer<typeof SkillScoreSchema>;

// ── Items ─────────────────────────────────────────────────────────────────────

export const ItemSenseSchema = z.object({
  gloss: z.string(),
  pos: z.string().nullable(),
  frequencyRank: z.number().int().nullable(),
});

export const DialectVariantSchema = z.object({
  dialect: DialectSchema,
  display: z.string(),
  note: z.string().nullable(),
});

export const ItemSchema = z.object({
  id: ContentIdSchema,
  type: ItemTypeSchema,
  language: LanguageSchema,
  dialect: DialectSchema.nullable(),
  display: z.string(),
  lemma: z.string(),
  ipa: z.string().nullable(),
  /** Learner-facing respelling, e.g. `kan-YON`. */
  respelling: z.string().nullable(),
  cefr: CefrSchema,
  frequencyRank: z.number().int().nullable(),
  senses: z.array(ItemSenseSchema),
  examples: z.array(
    z.object({
      text: z.string(),
      translation: z.string(),
      sourceId: ContentIdSchema.nullable(),
    }),
  ),
  /** Wedge W3: variants are surfaced on the word detail screen. */
  dialectVariants: z.array(DialectVariantSchema),
  audioUrl: MediaUrlSchema.nullable(),
  audioSlowUrl: MediaUrlSchema.nullable(),
});
export type Item = z.infer<typeof ItemSchema>;

export const CardStateSchema = z.object({
  itemId: ContentIdSchema,
  state: z.enum(['new', 'learning', 'review', 'relearning', 'suspended']),
  stability: z.number().nonnegative(),
  difficulty: z.number(),
  dueAt: EpochMsSchema,
  reps: z.number().int().nonnegative(),
  lapses: z.number().int().nonnegative(),
  /** Mastery requires this; see docs/05-content-pedagogy.md §4. */
  productionPassed: z.boolean(),
  intervalDays: z.number().nonnegative(),
  isMastered: z.boolean(),
  isLeech: z.boolean(),
});
export type CardState = z.infer<typeof CardStateSchema>;

// ── Speech feedback ───────────────────────────────────────────────────────────

export const DiagnosisSchema = z.object({
  name: z.string(),
  explanation: z.string(),
  remedy: z.string(),
  difficulty: z.enum(['quick', 'week', 'month', 'persistent']),
  minimalPairContrast: z.tuple([z.string(), z.string()]).nullable(),
  /** False when we fell back to a generic observation rather than a matched pattern. */
  matched: z.boolean(),
});

export const PhonemeFeedbackSchema = z.object({
  expected: z.string(),
  produced: z.string().nullable(),
  syllable: z.string().nullable(),
  score: Score100Schema,
  isError: z.boolean(),
  message: z.string(),
  diagnosis: DiagnosisSchema.nullable(),
  /** Model and learner audio for this phoneme, for A/B comparison. */
  coachAudioUrl: MediaUrlSchema.nullable(),
});

export const WordFeedbackSchema = z.object({
  word: z.string(),
  score: Score100Schema,
  band: ScoreBandSchema,
  /** A dimension no competitor except BoldVoice surfaces. */
  stressCorrect: z.boolean().nullable(),
  phonemes: z.array(PhonemeFeedbackSchema),
});

export const RemediationPlanSchema = z.object({
  phonemes: z.array(z.string()).max(3),
  estimatedMinutes: z.number().int().positive(),
  projectedAccuracyGain: z.number().nonnegative(),
});

export const UtteranceFeedbackSchema = z.object({
  utteranceId: UlidSchema,
  status: ScoreStatusSchema,
  /** Present when the score was withheld; names our own limitation, not a learner error. */
  withheldReason: z
    .enum([
      'too_quiet',
      'too_short',
      'no_speech',
      'clipped',
      'engines_disagree',
      'low_engine_confidence',
    ])
    .nullable(),
  message: z.string().nullable(),
  dimensions: ScoreDimensionsSchema.nullable(),
  overall: Score100Schema.nullable(),
  band: ScoreBandSchema.nullable(),
  transcript: z.string(),
  words: z.array(WordFeedbackSchema),
  remediation: RemediationPlanSchema.nullable(),
  /** Gated by the confidence decision; false means no phoneme claims are available. */
  hasPhonemeDetail: z.boolean(),
  /** More idiomatic phrasing, in the spirit of Loora's "Say it like a native speaker". */
  naturalAlternative: z
    .object({ text: z.string(), audioUrl: MediaUrlSchema.nullable() })
    .nullable(),
  learnerAudioUrl: MediaUrlSchema.nullable(),
  coachAudioUrl: MediaUrlSchema.nullable(),
  coachSlowAudioUrl: MediaUrlSchema.nullable(),
  /** Set once the learner has appealed; the mistake is withdrawn immediately. */
  appealedAt: EpochMsSchema.nullable(),
});
export type UtteranceFeedback = z.infer<typeof UtteranceFeedbackSchema>;

export const CorrectionSchema = z.object({
  original: z.string(),
  corrected: z.string(),
  kind: z.enum([
    'grammar',
    'word_choice',
    'register',
    'preposition',
    'gender',
    'agreement',
    'tense',
    'order',
  ]),
  explanation: z.string().max(220),
  severity: z.enum(['blocking', 'notable', 'nitpick']),
  itemIds: z.array(ContentIdSchema).max(5),
});
export type Correction = z.infer<typeof CorrectionSchema>;

// ── Sessions ──────────────────────────────────────────────────────────────────

export const TurnSchema = z.object({
  index: z.number().int().nonnegative(),
  tutorText: z.string(),
  tutorTranslation: z.string().nullable(),
  tutorAudioUrl: MediaUrlSchema.nullable(),
  learnerTranscript: z.string().nullable(),
  feedback: UtteranceFeedbackSchema.nullable(),
  corrections: z.array(CorrectionSchema),
  hintUsed: z.boolean(),
  translateUsed: z.boolean(),
});
export type Turn = z.infer<typeof TurnSchema>;

export const SessionSchema = z.object({
  id: UlidSchema,
  courseId: UlidSchema,
  kind: SessionKindSchema,
  /**
   * The lesson or scenario being practised, or null for open conversation.
   *
   * A content id, not a ULID — the same mismatch as the request body, one layer out. With
   * the body fixed, a scenario session started and then failed validating its own echo,
   * which is the shape of bug that breaks a client after the server has already done the
   * work.
   */
  refId: ContentIdSchema.nullable(),
  /**
   * The learner's own subject for this conversation — a debate topic and side, an
   * interview track — or null where the kind has no subject to choose.
   */
  subject: z.string().max(200).nullable(),
  mode: SessionModeSchema,
  state: z.enum(['active', 'paused', 'complete', 'abandoned']),
  turnIndex: z.number().int().nonnegative(),
  totalTurns: z.number().int().nonnegative(),
  voiceSeconds: z.number().int().nonnegative(),
  startedAt: EpochMsSchema,
  completedAt: EpochMsSchema.nullable(),
  /** Populated when the session had to fall back to text-only. */
  degradeReason: z
    .enum([
      'network',
      'provider_unavailable',
      'audio_session',
      'permission',
      'quota',
      'timeout',
      'unknown',
    ])
    .nullable(),
  rowV: z.number().int().positive(),
});
export type Session = z.infer<typeof SessionSchema>;

export const SuccessCriterionSchema = z.object({
  id: z.string(),
  description: z.string(),
  met: z.boolean(),
});

export const ReportCardSchema = z.object({
  sessionId: UlidSchema,
  kind: SessionKindSchema,
  dimensions: ScoreDimensionsSchema,
  /** Null when nothing in the session could be scored; never a 0 standing in for it. */
  overall: Score100Schema.nullable(),
  band: ScoreBandSchema.nullable(),
  /** Delta against the learner's own trailing average, per dimension. */
  deltas: ScoreDimensionsSchema.partial(),
  goalAchieved: z.enum(['yes', 'partial', 'no']),
  criteria: z.array(SuccessCriterionSchema),
  /** Prose written against what the learner actually said, per dimension. */
  notes: z.array(z.object({ dimension: z.string(), text: z.string() })),
  itemsLearned: z.array(z.object({ itemId: ContentIdSchema, display: z.string() })),
  mistakesFound: z.number().int().nonnegative(),
  durationSeconds: z.number().int().nonnegative(),
  turns: z.number().int().nonnegative(),
  /** Model re-render of the learner's best attempt (Stimuler's "see improved speech"). */
  improvedSpeechUrl: MediaUrlSchema.nullable(),
  /** The report card's primary CTA is always the drill, which closes the loop. */
  drillId: UlidSchema.nullable(),
  createdAt: EpochMsSchema,
});
export type ReportCard = z.infer<typeof ReportCardSchema>;

// ── Mistakes ──────────────────────────────────────────────────────────────────

export const MistakeSchema = z.object({
  id: UlidSchema,
  itemId: ContentIdSchema.nullable(),
  kind: z.enum(['pronunciation', 'grammar', 'vocabulary', 'word_choice', 'register', 'listening']),
  severity: z.enum(['blocking', 'notable', 'nitpick']),
  status: z.enum(['open', 'corrected', 'withdrawn']),
  /** Excerpt of the learner's own utterance, so the mistake is recognizable. */
  original: z.string(),
  corrected: z.string().nullable(),
  explanation: z.string(),
  phoneme: z.string().nullable(),
  /** The modality it must be passed in to close. */
  closeModality: ModalitySchema.nullable(),
  attempts: z.number().int().nonnegative(),
  openedAt: EpochMsSchema,
  closedAt: EpochMsSchema.nullable(),
});
export type Mistake = z.infer<typeof MistakeSchema>;

// ── Content ───────────────────────────────────────────────────────────────────

export const LessonSummarySchema = z.object({
  id: ContentIdSchema,
  moduleId: ContentIdSchema,
  title: z.string(),
  /** Always a doing-verb goal, enforced by the content validators. */
  goal: z.string(),
  index: z.number().int().positive(),
  totalInModule: z.number().int().positive(),
  estimatedSeconds: z.number().int().positive(),
  cefr: CefrSchema,
  state: z.enum(['locked', 'available', 'in_progress', 'complete', 'perfect']),
  lockedReason: z.string().nullable(),
  requiresVoice: z.boolean(),
  targetItems: z.array(z.object({ itemId: ContentIdSchema, display: z.string() })),
});
export type LessonSummary = z.infer<typeof LessonSummarySchema>;

export const ModuleSummarySchema = z.object({
  id: ContentIdSchema,
  title: z.string(),
  goal: z.string(),
  cefr: CefrSchema,
  lessonCount: z.number().int().nonnegative(),
  completedCount: z.number().int().nonnegative(),
  lessons: z.array(LessonSummarySchema),
});

export const ScenarioSchema = z.object({
  id: ContentIdSchema,
  title: z.string(),
  /** Mundane and specific, per the content standard — never an abstract topic. */
  brief: z.string(),
  yourRole: z.string(),
  theirRole: z.string(),
  category: z.enum([
    'daily_life',
    'work',
    'travel',
    'health',
    'services',
    'social',
    'family',
    'exam',
  ]),
  difficulty: z.enum(['easy', 'medium', 'hard']),
  cefr: CefrSchema,
  estimatedMinutes: z.tuple([z.number().int(), z.number().int()]),
  targetPhrases: z.array(
    z.object({ text: z.string(), translation: z.string(), audioUrl: MediaUrlSchema.nullable() }),
  ),
  /** Shown on the card, so the learner knows what they'll practise. */
  vocabPreview: z.array(z.string()).max(4),
  successCriteria: z.array(z.object({ id: z.string(), description: z.string() })),
  imageUrl: MediaUrlSchema.nullable(),
  isLocked: z.boolean(),
  isTrending: z.boolean(),
  /**
   * Present only on a scenario the caller wrote, `null` on everything else.
   *
   * The share control has to know whether this learner may share this scenario, and the
   * only honest source for that is the server — a client inferring authorship from "it
   * isn't in the catalogue" would offer a share button that fails on tap. `null` here is
   * the instruction not to show the control at all.
   */
  sharing: z
    .object({
      visibility: z.enum(['private', 'link']),
      /** Absent while private. Clients build the URL; the server owns the token. */
      shareToken: z.string().nullable(),
      /** How many other people have run it — the share loop's only feedback. */
      timesRun: z.number().int().nonnegative(),
    })
    .nullable(),
});
export type Scenario = z.infer<typeof ScenarioSchema>;

export const ListeningItemSchema = z.object({
  id: ContentIdSchema,
  title: z.string(),
  context: z.string(),
  level: z.enum(['beginner', 'intermediate', 'advanced']),
  cefr: CefrSchema,
  durationSeconds: z.number().int().positive(),
  speakerAccent: DialectSchema,
  audioUrl: MediaUrlSchema,
  transcript: z.array(
    z.object({
      startMs: z.number().int().nonnegative(),
      endMs: z.number().int().positive(),
      text: z.string(),
      translation: z.string(),
      /** Every token is tappable → word detail → add to deck. */
      tokens: z.array(z.object({ text: z.string(), itemId: ContentIdSchema.nullable() })),
    }),
  ),
  /** Set for the weekly news-sourced drops, which surface in the "This week" rail. */
  publishedAt: EpochMsSchema,
  isFresh: z.boolean(),
});
export type ListeningItem = z.infer<typeof ListeningItemSchema>;

export const GrammarConceptSchema = z.object({
  id: ContentIdSchema,
  name: z.string(),
  cefr: CefrSchema,
  explainer: z.string(),
  /** Contrast against the learner's own L1, which is a real pedagogical advantage. */
  l1Contrast: z.string().nullable(),
  examples: z.array(
    z.object({ text: z.string(), translation: z.string(), note: z.string().nullable() }),
  ),
  commonError: z.string().nullable(),
  mastery: Unit01Schema,
});

// ── Drills & review ───────────────────────────────────────────────────────────

export const DrillExerciseSchema = z.object({
  order: z.number().int().nonnegative(),
  /**
   * Null for an exercise built from a mistake rather than a catalogue item.
   *
   * Drills were assembled by joining `mistakes` to `items`, so a mistake made in
   * conversation — which has no item, being a sentence the learner said — could never
   * reach a drill, and the report card's primary action opened on nothing.
   */
  itemId: ContentIdSchema.nullable(),
  /** The mistake this practises, when it came from one, so passing it can close it. */
  mistakeId: UlidSchema.nullable(),
  type: z.enum([
    'mcq',
    'fillBlank',
    'reorder',
    'match',
    'dictation',
    'shadowing',
    'transform',
    'minimalPair',
  ]),
  prompt: z.string(),
  promptAudioUrl: MediaUrlSchema.nullable(),
  options: z.array(z.string()).nullable(),
  answer: z.string(),
  /** Explains *why*, not merely that the answer was wrong. */
  explanation: z.string(),
});

/**
 * What a finished drill changed.
 *
 * `mistakesClosed` counts rows the database actually changed rather than exercises
 * passed: the two differ exactly when `close_modality` refuses, and the learner is being
 * told how much shorter their worklist is.
 */
export const DrillResultsSchema = z.object({
  graded: z.number().int().nonnegative(),
  correct: z.number().int().nonnegative(),
  mistakesClosed: z.number().int().nonnegative(),
  mistakesOpen: z.number().int().nonnegative(),
});
export type DrillResults = z.infer<typeof DrillResultsSchema>;

export const DrillSchema = z.object({
  id: UlidSchema,
  sourceSessionId: UlidSchema.nullable(),
  exercises: z.array(DrillExerciseSchema),
  estimatedSeconds: z.number().int().nonnegative(),
});
export type Drill = z.infer<typeof DrillSchema>;

export const ReviewCardSchema = z.object({
  cardId: UlidSchema,
  itemId: ContentIdSchema,
  modality: ModalitySchema,
  reason: z.enum(['mistake', 'due', 'leech', 'new']),
  prompt: z.string(),
  promptAudioUrl: MediaUrlSchema.nullable(),
  answer: z.string(),
  options: z.array(z.string()).nullable(),
  /** Expected completion time, used to derive the FSRS grade from latency. */
  expectedMs: z.number().int().positive(),
});
export type ReviewCard = z.infer<typeof ReviewCardSchema>;

export const ReviewGradeSchema = z.object({
  cardId: UlidSchema,
  modality: ModalitySchema,
  correct: z.boolean(),
  elapsedMs: z.number().int().nonnegative(),
  hintUsed: z.boolean(),
  pronunciationScore: Score100Schema.optional(),
  /** Client-side timestamp, so offline reviews replay in the right order. */
  reviewedAt: EpochMsSchema,
});
export type ReviewGrade = z.infer<typeof ReviewGradeSchema>;

// ── Progress & gamification ───────────────────────────────────────────────────

export const StreakSchema = z.object({
  currentDays: z.number().int().nonnegative(),
  bestDays: z.number().int().nonnegative(),
  /** The forgiving second axis. */
  currentWeeks: z.number().int().nonnegative(),
  bestWeeks: z.number().int().nonnegative(),
  freezesLeft: z.number().int().nonnegative(),
  nextMilestone: z.object({ target: z.number().int(), remaining: z.number().int() }).nullable(),
  strip: z.array(
    z.object({
      localDate: LocalDateSchema,
      mark: z.enum(['done', 'missed', 'frozen', 'today', 'future']),
    }),
  ),
});
export type Streak = z.infer<typeof StreakSchema>;

export const UsageMeterSchema = z.object({
  /** `null` total means unmetered (Plus/Pro). */
  voiceSecondsUsed: z.number().int().nonnegative(),
  voiceSecondsTotal: z.number().int().nonnegative().nullable(),
  customScenariosUsed: z.number().int().nonnegative(),
  customScenariosTotal: z.number().int().nonnegative().nullable(),
  state: z.enum(['unlimited', 'ample', 'low', 'exhausted']),
  resetsAt: EpochMsSchema,
  /**
   * Seconds granted on top of today's allowance by the dismissal gift, already included
   * in `voiceSecondsTotal`. Carried separately so a client can say *why* the budget is
   * larger than the tier's — "5 extra minutes today, on us" — rather than silently
   * showing a number that does not match the plan.
   */
  bonusVoiceSeconds: z.number().int().nonnegative(),
});
export type UsageMeter = z.infer<typeof UsageMeterSchema>;

/**
 * Response of `claimDismissalGift`.
 *
 * `granted: false` with a reason is a normal answer, not an error: a paid learner, a
 * learner who already claimed it, or one who dismissed a paywall they were never blocked
 * by all get a considered no (`giftOnDismiss` in @vibelang/core).
 */
export const DismissalGiftResultSchema = z.object({
  granted: z.boolean(),
  seconds: z.number().int().nonnegative(),
  withheldReason: z
    .enum(['already_unmetered', 'already_claimed', 'did_not_dismiss', 'allowance_not_spent'])
    .nullable(),
  usage: UsageMeterSchema,
});

export const EntitlementSchema = z.object({
  tier: TierSchema,
  status: z.enum(['trialing', 'active', 'grace', 'expired', 'refunded', 'paused']).nullable(),
  periodEnd: EpochMsSchema.nullable(),
  inTrial: z.boolean(),
  inGrace: z.boolean(),
  /**
   * Which store holds the subscription, and therefore the only place it can be
   * cancelled. Null when there is nothing to cancel.
   *
   * Contracted rather than inferred because a client cannot know it: a learner who
   * subscribed on Android and opened the web app was being sent to Apple's
   * subscriptions page, where their subscription does not exist.
   */
  store: z.enum(['apple', 'google', 'stripe', 'promo']).nullable(),
  limits: z.object({
    voiceSecondsPerDay: z.number().nullable(),
    customScenariosPerDay: z.number().nullable(),
    historyDays: z.number().nullable(),
    scenarioCount: z.number().nullable(),
    deepAnalysis: z.boolean(),
    /** Debate and Interview: Plus and Pro, not free. */
    advancedModes: z.boolean(),
    offlineDownloads: z.boolean(),
    simultaneousCourses: z.number().int(),
    examTracks: z.boolean(),
    monthlyAssessment: z.boolean(),
  }),
});
export type Entitlement = z.infer<typeof EntitlementSchema>;

/**
 * Content disputes and referrals, named rather than inline.
 *
 * `dto-parity` compares a hand-written Swift or Kotlin DTO against the schema of the
 * same name, so a response shape declared inline in `routes.ts` is invisible to it. Five
 * DTOs added in October sat outside the gate for that reason, including the two whose
 * correctness I vouched for by citing the gate.
 */
export const ContentReportAckSchema = z.object({
  id: UlidSchema,
  withdrawn: z.boolean(),
  suppressedForYou: z.boolean(),
  message: z.string(),
});

export const ContentReportSummarySchema = z.object({
  id: UlidSchema,
  itemId: ContentIdSchema,
  reason: DisputeReasonSchema,
  note: z.string().nullable(),
  createdAt: EpochMsSchema,
  resolution: z.enum(['fixed', 'declined', 'withdrawn']).nullable(),
  resolvedAt: EpochMsSchema.nullable(),
});

export const ContentReportListSchema = z.object({
  reports: z.array(ContentReportSummarySchema),
  total: z.number().int().nonnegative(),
});

export const ReferralSchema = z.object({
  code: z.string().min(4),
  redeemed: z.number().int().nonnegative(),
  qualified: z.number().int().nonnegative(),
  monthsEarned: z.number().int().nonnegative(),
  requirement: z.string(),
});

export const ReferralRedemptionSchema = z.object({
  accepted: z.boolean(),
  message: z.string(),
});

export const AchievementSchema = z.object({
  key: z.string(),
  name: z.string(),
  tiers: z.array(
    z.object({
      tier: z.number().int().positive(),
      target: z.number().int().positive(),
      unlockedAt: EpochMsSchema.nullable(),
    }),
  ),
});

export const LeagueSchema = z.object({
  id: UlidSchema,
  weekStart: LocalDateSchema,
  /** Ranked on spoken minutes — the behaviour we actually want, not XP. */
  members: z.array(
    z.object({
      displayName: z.string(),
      spokenSeconds: z.number().int().nonnegative(),
      rank: z.number().int().positive(),
      isYou: z.boolean(),
    }),
  ),
  promotionRank: z.number().int().positive(),
  demotionRank: z.number().int().positive(),
});

// ── Today ─────────────────────────────────────────────────────────────────────

export const NextActionSchema = z.object({
  kind: z.enum([
    'review',
    'mistakes',
    'lesson',
    'drill',
    'scenario',
    'listen',
    'placement',
    'newContent',
    'weakSkill',
  ]),
  id: z.string(),
  title: z.string(),
  goal: z.string().nullable(),
  estimatedMinutes: z.number().int().positive(),
  requiresVoice: z.boolean(),
  route: z.string(),
  /** Why this was chosen, surfaced to the learner. */
  reason: z.string(),
  reasonCode: z.string(),
});
export type NextAction = z.infer<typeof NextActionSchema>;

export const FocusItemSchema = z.object({
  kind: z.enum(['phoneme', 'grammar', 'consistency', 'skill']),
  title: z.string(),
  detail: z.string(),
  estimatedMinutes: z.number().int().positive(),
  route: z.string(),
});

/**
 * The entire Today screen in one response. One round trip, ETag-cached, and all three
 * clients render the same screen from the same contract — no per-platform divergence in
 * what "Today" means (docs/07-tech-architecture.md §5).
 */
export const TodaySchema = z.object({
  courseId: UlidSchema,
  greetingName: z.string().nullable(),
  goal: z.object({
    minutes: z.number().int().positive(),
    activeSeconds: z.number().int().nonnegative(),
    met: z.boolean(),
    fraction: Unit01Schema,
  }),
  nextAction: NextActionSchema.nullable(),
  dueReviews: z.object({
    count: z.number().int().nonnegative(),
    estimatedMinutes: z.number().int().nonnegative(),
  }),
  openMistakes: z.object({
    total: z.number().int().nonnegative(),
    blocking: z.number().int().nonnegative(),
  }),
  streak: StreakSchema,
  usage: UsageMeterSchema,
  focus: z.array(FocusItemSchema).max(3),
  freshContent: z.object({
    count: z.number().int().nonnegative(),
    summary: z.string().nullable(),
  }),
  skillScores: z.array(SkillScoreSchema),
});
export type Today = z.infer<typeof TodaySchema>;

/**
 * Response shapes promoted out of `routes.ts`, one per route named in the comment.
 *
 * `dto-parity` compares a DTO against the schema of the same name, so a response
 * declared inline is a DTO nothing checks. These are the ones a hand-written Swift or
 * Kotlin DTO decodes; naming them is what brings those DTOs under the gate.
 */
/** Response of `getContentManifest`. */
export const ContentManifestSchema = z.object({
  version: z.number().int(),
  added: z.array(z.object({ kind: z.string(), id: ContentIdSchema, bytes: z.number().int() })),
  removed: z.array(z.object({ kind: z.string(), id: ContentIdSchema })),
  totalBytes: z.number().int(),
});

/** Response of `requestRefund`. */
export const RefundOutcomeSchema = z.object({
  storeRefundUrl: z.string().url(),
  /** Proactive goodwill for verified product failures. */
  goodwillGranted: z.boolean(),
  message: z.string(),
});

/** Response of `getExport`. */
export const ExportStatusSchema = z.object({
  requestId: UlidSchema,
  status: z.enum(['pending', 'ready', 'failed']),
  /** Set once ready: the export itself, as JSON. */
  bytes: z.number().int().nullable(),
  downloadUrl: z.string().nullable(),
  requestedAt: EpochMsSchema,
});

/** Response of `getCoursePath`. */
export const CoursePathSchema = z.object({
  currentLessonId: ContentIdSchema.nullable(),
  modules: z.array(ModuleSummarySchema),
});

/** Response of `getLesson`. */
export const LessonDetailSchema = z.object({
  lesson: LessonSummarySchema,
  targetItems: z.array(ItemSchema),
  /** Present when a prior attempt can be resumed rather than restarted. */
  resumableSessionId: UlidSchema.nullable(),
});

/** Response of `createSession`. */
export const SessionStartSchema = z.object({
  session: SessionSchema,
  firstTurn: TurnSchema.nullable(),
  usage: UsageMeterSchema,
  /** Pre-flight result; a failure names the specific cause, never a generic error. */
  preflight: z.object({
    ok: z.boolean(),
    problems: z.array(z.enum(['permission', 'device_busy', 'network', 'provider', 'quota'])),
  }),
});

/** Response of `getItem`. */
export const ItemDetailSchema = z.object({ item: ItemSchema, card: CardStateSchema.nullable() });

/** Response of `courseCatalogue`. */
export const CourseCatalogueSchema = z.object({
  dialect: DialectSchema,
  lessons: z.number().int().nonnegative(),
  scenarios: z.number().int().nonnegative(),
  items: z.number().int().nonnegative(),
});

/** Response of `appealScore`. */
export const AppealResultSchema = z.object({
  withdrawnMistakeIds: z.array(UlidSchema),
  recorded: z.boolean(),
  message: z.string(),
});

/** Response of `getReviewQueue`. */
export const ReviewQueueSchema = z.object({
  cards: z.array(ReviewCardSchema),
  dueTotal: z.number().int().nonnegative(),
  newIntroduced: z.number().int().nonnegative(),
  estimatedMinutes: z.number().int().nonnegative(),
  forecast: z.array(z.object({ dayOffset: z.number().int(), count: z.number().int() })),
});

/** Response of `getPronunciationClinic`. */
export const ClinicSchema = z.object({
  phonemes: z.array(
    z.object({
      phoneme: z.string(),
      errorCount: z.number().int().nonnegative(),
      meanScore: Score100Schema,
      /** Quantified, bounded remediation — Stimuler's pattern. */
      estimatedMinutes: z.number().int().positive(),
      projectedAccuracyGain: z.number(),
      articulation: z.string(),
      minimalPairs: z.array(z.tuple([z.string(), z.string()])),
      /**
       * Words that differ only by this contrast, for E4's "hear two, pick which".
       *
       * `minimalPairs` above is a pair of phoneme *symbols*, which the clinic rendered as
       * text — "Minimal pairs: /r/ vs /rr/". A learner cannot practise a symbol. These are
       * actual words, curated in `packages/core` and empty where there is no honest pair:
       * Spanish has merged `ʎ`/`j` for most speakers and `β`/`v` are one phoneme, so
       * drilling either would be teaching a distinction the language does not make.
       *
       * Audio is present only when the request named a single `phoneme`, because
       * synthesising every word of all eight sounds would be dozens of vendor calls on one
       * request. The list view shows the words; the one-sound view hears them.
       */
      pairs: z.array(
        z.object({
          a: z.string(),
          b: z.string(),
          gloss: z.string(),
          audioA: MediaUrlSchema.nullable(),
          audioB: MediaUrlSchema.nullable(),
        }),
      ),
      history: z.array(z.object({ at: EpochMsSchema, score: Score100Schema })),
    }),
  ),
});

/** Response of `getCertificate`. */
export const CertificateSchema = z.object({
  /** False until the level estimate is confident enough to put a name to. */
  earned: z.boolean(),
  level: CefrSchema.nullable(),
  /** What it takes, stated, when it is not earned yet. */
  requires: z.string().nullable(),
  spokenHours: z.number().nonnegative(),
  topSkills: z.array(z.object({ pillar: z.string(), score: Score100Schema })),
  issuedOn: LocalDateSchema.nullable(),
  /** Null until earned. Inline SVG, so sharing needs no second request. */
  svg: z.string().nullable(),
});

/** A day's figure in one of F1's series. */
export const DailyMinutesSchema = z.object({
  localDate: LocalDateSchema,
  minutes: z.number(),
});

export const DailyAccuracySchema = z.object({
  localDate: LocalDateSchema,
  accuracy: Score100Schema,
});

/** Lifetime counts for F1's header. */
export const ProgressTotalsSchema = z.object({
  lessonsComplete: z.number().int(),
  wordsMastered: z.number().int(),
  spokenSeconds: z.number().int(),
  sessions: z.number().int(),
});

/**
 * One finished session, for F1's history list.
 *
 * The facts of the session — when, how long, how many turns, how it scored — and not
 * the report card's analysis, which stays behind `listReports` and its tier window.
 */
export const SessionSummarySchema = z.object({
  sessionId: UlidSchema,
  kind: SessionKindSchema,
  mode: SessionModeSchema,
  startedAt: EpochMsSchema,
  minutes: z.number().nonnegative(),
  turns: z.number().int().nonnegative(),
  /** Mean accuracy, or null: a text session, or every turn withheld. */
  accuracy: Score100Schema.nullable(),
});
export type SessionSummary = z.infer<typeof SessionSummarySchema>;

/** One past level estimate. Named so both the detail and the progress home share it. */
export const CefrHistoryEntrySchema = z.object({
  at: EpochMsSchema,
  level: CefrSchema,
  confidence: Unit01Schema,
  /** What produced it — a placement probe, a session, a monthly assessment. */
  source: z.string(),
});
export type CefrHistoryEntry = z.infer<typeof CefrHistoryEntrySchema>;

/**
 * Response of `getProgress` — F1's whole screen.
 *
 * Named rather than inlined in `routes.ts` so `dto-parity` compares the mobile DTOs
 * against it. It was inline while neither phone decoded it at all, and the two facts
 * are related: nothing was watching.
 */
export const ProgressHomeSchema = z.object({
  cefrLevel: CefrSchema,
  cefrConfidence: Unit01Schema,
  cefrBandSublevels: z.number().int(),
  /**
   * The last two dozen level estimates, **newest first**, for F1's sparkline.
   *
   * Capped rather than unbounded: estimates come from placements, retests and monthly
   * assessments, so two dozen is years of them, and a chart of hundreds of points in a
   * badge-sized space is not a chart. The same order as `getCefrDetail.history`, which
   * is to say a chart drawn from it reverses it.
   */
  cefrHistory: z.array(CefrHistoryEntrySchema),
  skills: z.array(SkillScoreSchema),
  /** Our north-star metric, shown to the learner. */
  spokenMinutes: z.array(DailyMinutesSchema),
  /**
   * Minutes spoken in the last seven *calendar* days, computed by the server.
   *
   * Sent rather than derived, because all three clients derived it wrongly and in two
   * different ways. Both phones reported *today's* minutes under this name. The web
   * read the series, which looked right, and took its last seven entries — but
   * `daily_activity` holds a row only for a day the learner was active, so seven
   * sessions spread over three months were summed as "the last seven days".
   *
   * The server already knows the learner's UTC offset and therefore which seven days
   * those are, so the figure has one definition in one place and cannot differ by
   * platform. It does not change with `window`.
   */
  spokenMinutes7d: z.number(),
  accuracyTrend: z.array(DailyAccuracySchema),
  streak: StreakSchema,
  totals: ProgressTotalsSchema,
  /**
   * The last ten finished sessions, newest first, within the tier's history window.
   *
   * Capped and windowed by the same `LIMITS[tier].historyDays` rule as `listReports`,
   * through `historyHorizon`, so the progress home cannot become a way around the
   * report-card archive's paywall. These are the session's own facts rather than its
   * analysis; the analysis is still only on `listReports`.
   */
  recentSessions: z.array(SessionSummarySchema),
  /** True when sessions exist beyond the window — drives the same `history_7d` prompt. */
  sessionsTruncatedByTier: z.boolean(),
});
export type ProgressHome = z.infer<typeof ProgressHomeSchema>;

/** Response of `getCefrDetail`. */
/**
 * The measured window behind a forward projection.
 *
 * Only the *measurement* travels: how many sub-levels the learner gained, how many
 * minutes they actually practised, over how long. The projection itself is computed on
 * the client, because it also depends on the daily goal the learner is looking at — so
 * the timeline moves the moment they change that goal rather than on the next fetch.
 * `projectProgress` in @vibelang/core does the arithmetic, and the Swift and Kotlin ports
 * assert the same cases.
 *
 * Null when there is no window worth dividing by, which is the common case early on.
 * Every competitor draws the line anyway (docs/research/16-category-leaders-2026-10-20.md
 * §4); a null here is what lets us decline instead.
 */
export const PaceWindowSchema = z.object({
  /** Sub-levels gained across the window. May be 0 or negative; the client decides. */
  sublevelsGained: z.number(),
  /** Minutes actually practised in the window — spoken seconds, not the daily goal. */
  practiceMinutes: z.number().nonnegative(),
  windowDays: z.number().int().nonnegative(),
});

export const CefrDetailSchema = z.object({
  level: CefrSchema,
  confidence: Unit01Schema,
  bandSublevels: z.number().int(),
  bandLabel: z.string(),
  canDo: z.array(z.string()),
  cannotYet: z.array(z.string()),
  evidence: z.array(z.object({ component: z.string(), score: z.number(), weight: z.number() })),
  sampleSize: z.number().int(),
  /**
   * Every estimate this learner has had, **newest first**.
   *
   * The order is stated here because it was only ever implied by an `ORDER BY
   * created_at DESC` in `routes/progress.ts`, and both phones then read
   * `history.last` as the latest estimate and reported the *oldest* one's source in
   * `cefr_detail_view {source}` — a wrong value in the event that exists to tell us
   * whether the level is believed. A chart drawn from this reverses it.
   */
  history: z.array(CefrHistoryEntrySchema),
  retestAvailable: z.boolean(),
  /** Evidence for the forward projection, or null when we will not project. */
  paceWindow: PaceWindowSchema.nullable(),
});

/** Response of `trackEvents`. */
export const IngestedSchema = z.object({
  accepted: z.number().int().nonnegative(),
  /** Opted out, a duplicate id, or an event the catalogue does not declare. */
  discarded: z.number().int().nonnegative(),
  /** Names the Worker refused, so a client bug is visible rather than silent. */
  rejectedNames: z.array(z.string()),
});

/** Response of `getConjugation`. */
export const ConjugationGridSchema = z.object({
  lemma: z.string(),
  found: z.boolean(),
  /** Ordered as the grid is drawn; empty when the verb has no conjugation data. */
  cells: z.array(
    z.object({
      tense: z.string(),
      person: z.string(),
      form: z.string(),
      /** True when this cell does not follow the regular pattern. */
      irregular: z.boolean(),
      attempts: z.number().int().nonnegative(),
      correct: z.number().int().nonnegative(),
    }),
  ),
  /** The cells worth drilling, weakest first. Empty is a valid, stated answer. */
  weakest: z.array(z.object({ tense: z.string(), person: z.string() })),
});
